import { supabase as defaultSupabase } from "../../utils/supabase";
import {
  getTrustedHistoryMetricsForDate,
  pickHistoryForDate,
} from "./buildTrustedHistory";
import { getWorkoutSaveGuardDecision } from "./workoutSaveGuards";
import { logWriteTrace, wpCountExercisesFromHistory, wpCountSetsFromHistory } from "../../utils/writeTrace";

const normalizeDateKey = (value) => String(value || "").slice(0, 10);

const requireUserAndDate = (userId, date) => {
  const normalizedDate = normalizeDateKey(date);
  if (!userId) throw new Error("workoutRepository requires userId");
  if (!normalizedDate) throw new Error("workoutRepository requires date");
  return normalizedDate;
};

// ─── Private: reads ONLY from workouts.data (no normalized tables) ────────────
async function fetchWorkoutRowForDateFromStorage(supabase, userId, date) {
  const { data, error } = await supabase
    .from("workouts")
    .select("id,user_id,date,data,created_at")
    .eq("user_id", userId)
    .eq("date", date)
    .maybeSingle();
  return { row: data || null, error };
}

/**
 * Public: fetch directly from workouts.data — never goes through normalized tables.
 * Use for guard comparisons where normalized tables may be stale (e.g. deleted exercises
 * that were not propagated to workout_exercises).
 */
export async function fetchWorkoutRowFromWorkoutsData({
  userId,
  date,
  supabase: supabaseClient = defaultSupabase,
} = {}) {
  const normalizedDate = requireUserAndDate(userId, date);
  return fetchWorkoutRowForDateFromStorage(supabaseClient, userId, normalizedDate);
}

// ─── Pure: build a single workout_sets row from one set object ────────────────
// 切り出し理由: saveExercisesAndSets は Supabase を呼ぶため直接テストできない。
// この関数だけを export してカラム集合をテストする。
// ── 制約 ──────────────────────────────────────────────────────────────────────
// 呼び出し元は saveExercisesAndSets の .map() 1箇所のみ。
// 出力は切り出し前の inline オブジェクトリテラルと完全に同一。
export function buildWorkoutSetRow(s, idx, { exercise_id, user_id }) {
  return {
    exercise_id,
    user_id,
    set_number:     idx + 1,
    weight:         s.weight === "BW" ? null : (Number(s.weight) || null),
    reps:           Number(s.reps) || 0,
    unit:           s.unit || s.weightUnit || (s.weight === "BW" ? "BW" : "kg"),
    display_weight: s.displayWeight != null ? Number(s.displayWeight) : (s.weight === "BW" ? null : Number(s.weight) || null),
    display_unit:   s.displayUnit || s.unit || "kg",
    added_weight_kg: s.added_weight_kg != null ? Number(s.added_weight_kg) : null,
  };
}

// ─── New helper: save to workout_exercises + workout_sets ─────────────────────
async function saveExercisesAndSets(supabase, workoutId, userId, date, historyForDate) {
  // historyForDate is like: { "Bench Press": [{date, bodyPart, order, sets:[...]}], ... }
  const exerciseEntries = Object.entries(historyForDate || {});

  // Delete exercises that are no longer in historyForDate.
  // saveExercisesAndSets only upserts; without this delete, removed exercises
  // remain in workout_exercises and cause stale 9-exercise reads when
  // fetchNormalizedWorkoutRow is called after a delete write.
  const currentExerciseNames = new Set(exerciseEntries.map(([name]) => name));
  const { data: existingExercises } = await supabase
    .from("workout_exercises")
    .select("id, exercise_name")
    .eq("workout_id", workoutId);
  const toDeleteIds = (existingExercises || [])
    .filter(ex => !currentExerciseNames.has(ex.exercise_name))
    .map(ex => ex.id)
    .filter(Boolean);
  if (toDeleteIds.length > 0) {
    await supabase.from("workout_exercises").delete().in("id", toDeleteIds);
  }

  for (let displayOrder = 0; displayOrder < exerciseEntries.length; displayOrder++) {
    const [exerciseName, records] = exerciseEntries[displayOrder];

    // Find the record for this date
    const record = (records || []).find(r => String(r?.date || "").slice(0, 10) === date)
      || records?.[0];
    if (!record || !Array.isArray(record.sets) || record.sets.length === 0) continue;

    // Upsert workout_exercises
    const { data: exRow, error: exErr } = await supabase
      .from("workout_exercises")
      .upsert({
        workout_id:    workoutId,
        exercise_name: exerciseName,
        body_part:     record.bodyPart || null,
        display_order: typeof record.order === "number" ? record.order : displayOrder,
      }, { onConflict: "workout_id,exercise_name" })
      .select("id")
      .single();

    if (exErr || !exRow?.id) continue;

    // Delete existing sets (replace strategy)
    await supabase
      .from("workout_sets")
      .delete()
      .eq("exercise_id", exRow.id);

    // Insert sets
    const setsToInsert = record.sets
      .filter(s => s && (Number(s.reps) > 0 || s.weight === "BW"))
      .map((s, idx) => buildWorkoutSetRow(s, idx, { exercise_id: exRow.id, user_id: userId }));

    if (setsToInsert.length > 0) {
      await supabase.from("workout_sets").insert(setsToInsert);
    }
  }
}

// ─── New helper: reconstruct data-compatible object from normalized rows ───────
export function buildDataFromNormalizedRows(exercises, sets, date) {
  // exercises: [{id, workout_id, exercise_name, body_part, display_order}]
  // sets: [{id, exercise_id, set_number, weight, reps, unit, display_weight, display_unit}]
  // Returns: { "Bench Press": [{date, bodyPart, order, sets:[...]}], ... }

  const setsByExerciseId = {};
  (sets || []).forEach(s => {
    if (!setsByExerciseId[s.exercise_id]) setsByExerciseId[s.exercise_id] = [];
    setsByExerciseId[s.exercise_id].push(s);
  });

  const data = {};
  (exercises || [])
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    .forEach(ex => {
      const exSets = (setsByExerciseId[ex.id] || [])
        .sort((a, b) => a.set_number - b.set_number)
        .map(s => ({
          weight:           s.weight != null ? s.weight : (s.unit === "BW" ? "BW" : 0),
          reps:             s.reps,
          unit:             s.unit || "kg",
          displayWeight:    s.display_weight != null ? s.display_weight : s.weight,
          displayUnit:      s.display_unit || s.unit || "kg",
          added_weight_kg:  s.added_weight_kg ?? null,
          done:             true,
        }));

      if (exSets.length === 0) return;

      data[ex.exercise_name] = [{
        date:        date,
        workoutDate: date,
        bodyPart:    ex.body_part || null,
        order:       ex.display_order || 0,
        sets:        exSets,
      }];
    });

  return data;
}

// ─── New helper: read from new tables, return row in workouts shape ───────────
async function fetchNormalizedWorkoutRow(supabase, userId, date) {
  // 1. Get the workout row
  const { data: workout, error: wErr } = await supabase
    .from("workouts")
    .select("id,user_id,date,created_at,duration_sec,updated_at")
    .eq("user_id", userId)
    .eq("date", date)
    .maybeSingle();

  if (wErr || !workout) return { row: null, error: wErr };

  // 2. Get exercises
  const { data: exercises, error: exErr } = await supabase
    .from("workout_exercises")
    .select("id,workout_id,exercise_name,body_part,display_order")
    .eq("workout_id", workout.id);

  if (exErr) return { row: null, error: exErr };
  if (!exercises || exercises.length === 0) return { row: null, error: null }; // no normalized data yet

  // 3. Get sets
  const exerciseIds = exercises.map(e => e.id);
  const { data: sets, error: setErr } = await supabase
    .from("workout_sets")
    .select("id,exercise_id,set_number,weight,reps,unit,display_weight,display_unit,added_weight_kg")
    .in("exercise_id", exerciseIds);

  if (setErr) return { row: null, error: setErr };

  // 4. Reconstruct data field
  const data = buildDataFromNormalizedRows(exercises, sets || [], date);

  return {
    row: {
      id:           workout.id,
      user_id:      workout.user_id,
      date:         workout.date,
      data,
      created_at:   workout.created_at,
      duration_sec: workout.duration_sec,
      updated_at:   workout.updated_at,
    },
    error: null,
  };
}

// ─── AI用: JSONB + 正規化テーブル両対応のバルク取得 ──────────────────────────
// workouts.data が null の行（新フォーマット）は workout_exercises/workout_sets から
// data を再構築して返す。常に新しい順で最大 limit 件。
export async function fetchWorkoutRowsForAI({
  userId,
  isPro = false,
  threeMonthsAgoKey = "",
  limit = 150,
  supabase = defaultSupabase,
} = {}) {
  if (!userId) return { rows: [], error: null };

  // Step 1: workouts テーブルから id/date/data を取得（新しい順）
  let query = supabase
    .from("workouts")
    .select("id,date,data")
    .eq("user_id", userId)
    .order("date", { ascending: false })
    .limit(limit);

  if (!isPro && threeMonthsAgoKey) {
    query = query.gte("date", threeMonthsAgoKey);
  }

  const { data: workoutRows, error } = await query;
  if (error) return { rows: [], error };

  const rows = workoutRows || [];

  // Step 2: data が null/非オブジェクト の行を正規化テーブルから再構築
  const needNormalized = rows.filter(
    (r) => !r.data || typeof r.data !== "object" || Array.isArray(r.data)
  );

  if (needNormalized.length === 0) return { rows, error: null };

  const normalizedIds = needNormalized.map((r) => r.id).filter(Boolean);

  const { data: exercises } = await supabase
    .from("workout_exercises")
    .select("id,workout_id,exercise_name,body_part,display_order")
    .in("workout_id", normalizedIds);

  if (!exercises || exercises.length === 0) return { rows, error: null };

  const exerciseIds = exercises.map((e) => e.id);
  const { data: sets } = await supabase
    .from("workout_sets")
    .select("id,exercise_id,set_number,weight,reps,unit,display_weight,display_unit,added_weight_kg")
    .in("exercise_id", exerciseIds);

  // workout_id → exercises のマップ
  const exercisesByWorkoutId = {};
  for (const ex of exercises) {
    if (!exercisesByWorkoutId[ex.workout_id]) exercisesByWorkoutId[ex.workout_id] = [];
    exercisesByWorkoutId[ex.workout_id].push(ex);
  }

  const normalizedIdSet = new Set(normalizedIds);
  const reconstructed = rows.map((row) => {
    if (!normalizedIdSet.has(row.id)) return row;
    return {
      id: row.id,
      date: row.date,
      data: buildDataFromNormalizedRows(
        exercisesByWorkoutId[row.id] || [],
        sets || [],
        row.date,
      ),
    };
  });

  return { rows: reconstructed, error: null };
}

export async function fetchWorkoutRows({
  userId,
  fromDate = "",
  toDate = "",
  limit = 120,
  supabase = defaultSupabase,
} = {}) {
  if (!userId) return { rows: [], error: null };

  let query = supabase
    .from("workouts")
    .select("id,user_id,date,data,created_at")
    .eq("user_id", userId)
    .order("date", { ascending: true });

  if (fromDate) query = query.gte("date", normalizeDateKey(fromDate));
  if (toDate) query = query.lte("date", normalizeDateKey(toDate));
  if (limit) query = query.limit(limit);

  const { data, error } = await query;
  return { rows: data || [], error };
}

export async function fetchWorkoutRowsForDates({
  userId,
  dates = [],
  supabase = defaultSupabase,
} = {}) {
  const normalizedDates = [...new Set(
    (dates || []).map(normalizeDateKey).filter(Boolean)
  )];
  if (!userId || !normalizedDates.length) return { rows: [], error: null };

  const { data, error } = await supabase
    .from("workouts")
    .select("id,user_id,date,data,created_at")
    .eq("user_id", userId)
    .in("date", normalizedDates)
    .order("date", { ascending: true });

  return { rows: data || [], error };
}

export async function fetchWorkoutRowForDate({
  userId,
  date,
  supabase = defaultSupabase,
} = {}) {
  const normalizedDate = requireUserAndDate(userId, date);

  // Try normalized tables first
  const normalized = await fetchNormalizedWorkoutRow(supabase, userId, normalizedDate);
  if (normalized.error) return { row: null, error: normalized.error };
  if (normalized.row) return normalized;

  // Fall back to workouts.data
  return fetchWorkoutRowForDateFromStorage(supabase, userId, normalizedDate);
}

export async function saveWorkoutForDate({
  userId,
  date,
  history,
  remoteHistory = {},
  reason = "",
  explicitEdit = false,
  explicitDelete = false,
  deleteScope = null,
  source = "workoutRepository",
  logger = console,
  supabase = defaultSupabase,
} = {}) {
  const startedAt = (
    typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : Date.now()
  );
  const normalizedDate = requireUserAndDate(userId, date);
  const dateScopedHistory = pickHistoryForDate(history || {}, normalizedDate);
  const remoteDateScopedHistory = pickHistoryForDate(remoteHistory || {}, normalizedDate);

  logWriteTrace("repository", {
    date: normalizedDate,
    decision: "pass",
    reason: reason || null,
    flags: { explicitEdit, explicitDelete, source },
    exerciseCount: wpCountExercisesFromHistory(dateScopedHistory, normalizedDate),
    setCount: wpCountSetsFromHistory(dateScopedHistory, normalizedDate),
  });

  const guard = getWorkoutSaveGuardDecision({
    localHistory: dateScopedHistory,
    remoteHistory: remoteDateScopedHistory,
    date: normalizedDate,
    reason,
    explicitEdit,
    explicitDelete,
  });

  logWriteTrace("G2:repository-guard", {
    date: normalizedDate,
    decision: guard.blocked ? "block" : "pass",
    reason: guard.blocked ? (guard.blockedReason || null) : (guard.allowedReason || null),
    flags: { explicitEdit, explicitDelete, source },
    exerciseCount: guard.localMetrics?.exerciseCount ?? null,
    setCount: guard.localMetrics?.setCount ?? null,
    extra: {
      remoteExerciseCount: guard.remoteMetrics?.exerciseCount ?? null,
      removedExerciseNames: guard.details?.removedExerciseNames,
    },
  });

  if (guard.blocked) {
    logger?.warn?.("[workout repository] save blocked by guard", {
      action: "workout_repository_save",
      date: normalizedDate,
      source,
      beforeExerciseNames: guard.localMetrics.exerciseNames,
      savedExerciseNames: guard.localMetrics.exerciseNames,
      remoteVerifiedExerciseNames: guard.remoteMetrics.exerciseNames,
      lostExerciseNames: guard.details.removedExerciseNames,
      blockedRegression: true,
      blockedReason: guard.blockedReason,
      durationMs: Math.round((
        (typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now()) - startedAt
      ) * 10) / 10,
    });
    return {
      ok: false,
      skipped: true,
      blocked: true,
      error: null,
      guard,
      verifiedRow: null,
      verifiedHistory: {},
      savedHistory: dateScopedHistory,
    };
  }

  const hasWorkout = guard.localMetrics.hasWorkout;

  // G13: 日付全体の削除（deleteScope === "date"）かつ hasWorkout は矛盾した状態を示す。
  // 正常な全削除では payload が空になるため hasWorkout = false になるはず。
  // hasWorkout = true のままここに到達した場合、stale payload が
  // pendingSavePayloadsRef に残っている可能性が高い。
  // deleteScope === "partial" の部分削除では hasWorkout = true が正常なため除外する。
  // 現時点では delete 分岐への強制切り替えはせず、ログのみ出力する（決定は呼び出し元）。
  if (explicitDelete && hasWorkout && deleteScope === "date") {
    logWriteTrace("G13:delete-payload-contradiction", {
      date: normalizedDate,
      decision: "pass",
      reason: "explicitDelete is true but hasWorkout is true — payload may be stale",
      flags: { explicitEdit, explicitDelete, source },
      exerciseCount: guard.localMetrics?.exerciseCount ?? null,
      setCount: guard.localMetrics?.setCount ?? null,
    });
  }

  if (hasWorkout) {
    logWriteTrace("supabase:upsert", {
      date: normalizedDate,
      decision: "executed",
      reason: reason || null,
      flags: { explicitEdit, explicitDelete, source },
      exerciseCount: guard.localMetrics?.exerciseCount ?? null,
      setCount: guard.localMetrics?.setCount ?? null,
    });
  } else if (explicitDelete) {
    logWriteTrace("supabase:delete", {
      date: normalizedDate,
      decision: "executed",
      reason: reason || null,
      flags: { explicitEdit, explicitDelete, source },
      exerciseCount: 0,
      setCount: 0,
    });
  } else {
    logWriteTrace("supabase:refused", {
      date: normalizedDate,
      decision: "block",
      reason: "Refusing to save empty workout without explicit delete",
      flags: { explicitEdit, explicitDelete, source },
      exerciseCount: 0,
      setCount: 0,
    });
  }
  const saveResponse = hasWorkout
    ? await supabase
        .from("workouts")
        .upsert({
          user_id: userId,
          date: normalizedDate,
          data: dateScopedHistory,
        }, { onConflict: "user_id,date" })
      : explicitDelete
        ? await supabase
          .from("workouts")
          .delete()
          .eq("user_id", userId)
          .eq("date", normalizedDate)
        : { error: new Error("Refusing to save empty workout without explicit delete") };

  if (saveResponse.error) {
    logger?.warn?.("[workout repository] save failed", {
      action: "workout_repository_save",
      date: normalizedDate,
      source,
      beforeExerciseNames: guard.localMetrics.exerciseNames,
      savedExerciseNames: guard.localMetrics.exerciseNames,
      remoteVerifiedExerciseNames: guard.remoteMetrics.exerciseNames,
      lostExerciseNames: [],
      blockedRegression: false,
      error: saveResponse.error?.message || String(saveResponse.error),
      durationMs: Math.round((
        (typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now()) - startedAt
      ) * 10) / 10,
    });
    return {
      ok: false,
      error: saveResponse.error,
      guard,
      verifiedRow: null,
      savedHistory: dateScopedHistory,
    };
  }

  // Dual-write to normalized tables (best-effort, non-fatal)
  if (hasWorkout) {
    try {
      // Fetch the workout id from storage (not via normalized path to avoid loop)
      const { row: storageRow } = await fetchWorkoutRowForDateFromStorage(supabase, userId, normalizedDate);
      if (storageRow?.id) {
        await saveExercisesAndSets(
          supabase,
          storageRow.id,
          userId,
          normalizedDate,
          dateScopedHistory
        );
      }
    } catch (normalizedError) {
      logger?.warn?.("[workout repository] normalized save failed (non-fatal)", {
        error: normalizedError?.message,
        date: normalizedDate,
      });
    }
  }

  // Read back from workouts.data directly (never normalized tables — they may be stale)
  const verify = await fetchWorkoutRowForDateFromStorage(supabase, userId, normalizedDate);
  const verifiedHistory = verify.row?.data
    ? pickHistoryForDate(verify.row.data, normalizedDate)
    : {};
  const verifiedMetrics = getTrustedHistoryMetricsForDate(verifiedHistory, normalizedDate);
  const savedNameSet = new Set((guard.localMetrics.exerciseNames || []).map((name) => String(name || "").trim()).filter(Boolean));
  const verifiedNameSet = new Set((verifiedMetrics.exerciseNames || []).map((name) => String(name || "").trim()).filter(Boolean));
  const lostExerciseNames = [...savedNameSet].filter((name) => !verifiedNameSet.has(name));
  const exerciseCountMismatch = hasWorkout && verifiedMetrics.exerciseCount !== guard.localMetrics.exerciseCount;
  const verifyFailed = Boolean(
    verify.error ||
    (hasWorkout && (
      lostExerciseNames.length ||
      exerciseCountMismatch ||
      verifiedMetrics.setCount < guard.localMetrics.setCount
    ))
  );

  logger?.log?.("[workout repository] save verified", {
    action: "workout_repository_save",
    date: normalizedDate,
    source,
    beforeExerciseNames: guard.localMetrics.exerciseNames,
    savedExerciseNames: guard.localMetrics.exerciseNames,
    remoteVerifiedExerciseNames: verifiedMetrics.exerciseNames,
    lostExerciseNames,
    blockedRegression: verifyFailed,
    blockedReason: verifyFailed && !verify.error
      ? "remote verification did not match saved workout"
      : null,
    durationMs: Math.round((
      (typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now()) - startedAt
    ) * 10) / 10,
  });

  logWriteTrace("verify", {
    date: normalizedDate,
    decision: verifyFailed ? "block" : "pass",
    reason: verifyFailed
      ? (verify.error ? `fetch error: ${verify.error?.message || String(verify.error)}` : "remote verification did not match saved workout")
      : null,
    flags: { explicitEdit, explicitDelete, source },
    exerciseCount: verifiedMetrics.exerciseCount ?? null,
    setCount: verifiedMetrics.setCount ?? null,
    extra: {
      lostExerciseNames,
      exerciseCountMismatch,
      verifyFailed,
      savedExerciseCount: guard.localMetrics?.exerciseCount ?? null,
      savedSetCount: guard.localMetrics?.setCount ?? null,
    },
  });

  return {
    ok: !verifyFailed,
    error: verify.error || (verifyFailed ? new Error(`workouts.data verification failed for ${normalizedDate}`) : null),
    guard,
    verifiedRow: verify.row,
    verifiedHistory,
    verifiedMetrics,
    savedHistory: dateScopedHistory,
  };
}

// ─── Pending delete queue (for pagehide / visibilitychange flush) ─────────────
// key → { userId, date }
const _pendingDeletes = new Map();

export function registerPendingDelete(userId, date) {
  const key = `${userId}:${String(date || "").slice(0, 10)}`;
  _pendingDeletes.set(key, { userId, date: String(date || "").slice(0, 10) });
}

export function clearPendingDelete(userId, date) {
  const key = `${userId}:${String(date || "").slice(0, 10)}`;
  _pendingDeletes.delete(key);
}

// Internal: parallel delete from workouts + workout_exercises, then verify
async function _executeDeleteForDate(supabase, userId, date) {
  // Get workout id so we can explicitly delete workout_exercises in parallel
  const { data: workoutRow } = await supabase
    .from("workouts")
    .select("id")
    .eq("user_id", userId)
    .eq("date", date)
    .maybeSingle();

  // Parallel: workouts row (cascades to workout_exercises → workout_sets)
  //           + explicit workout_exercises delete (belt-and-suspenders)
  const ops = [
    supabase.from("workouts").delete().eq("user_id", userId).eq("date", date),
  ];
  if (workoutRow?.id) {
    ops.push(
      supabase.from("workout_exercises").delete().eq("workout_id", workoutRow.id)
    );
  }

  const results = await Promise.all(ops);
  const error = results.find((r) => r.error)?.error || null;
  if (error) return { ok: false, error };

  // Verify the row is gone
  const { data: remaining } = await supabase
    .from("workouts")
    .select("id")
    .eq("user_id", userId)
    .eq("date", date)
    .maybeSingle();

  if (remaining) {
    return {
      ok: false,
      error: new Error(`deleteWorkoutForDate: row still exists after delete for ${date}`),
    };
  }

  return { ok: true, error: null };
}

// Flush all registered pending deletes (fire-and-forget; best-effort)
export async function flushPendingDeletes() {
  if (_pendingDeletes.size === 0) return;
  const items = [..._pendingDeletes.values()];
  // Don't clear map here — let clearPendingDelete handle it after each completes
  await Promise.all(
    items.map(({ userId, date }) =>
      _executeDeleteForDate(defaultSupabase, userId, date)
        .then(() => clearPendingDelete(userId, date))
        .catch(() => {})
    )
  );
}

// Register page lifecycle listeners once at module load
if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushPendingDeletes();
    }
  });
  window.addEventListener("pagehide", () => {
    flushPendingDeletes();
  });
}

export async function deleteWorkoutForDate({
  userId,
  date,
  supabase = defaultSupabase,
} = {}) {
  const normalizedDate = requireUserAndDate(userId, date);
  const result = await _executeDeleteForDate(supabase, userId, normalizedDate);
  clearPendingDelete(userId, normalizedDate);
  return result;
}
